/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  id: yup.number()
    .positive('O ID deve ser um número positivo')
    .nullable(),
  name: yup
    .string()
    .required('O nome é obrigatório')
    .default('')
    .min(0, 'O nome deve ter no mínimo 0 caracteres')
    .max(255, 'O nome deve ter no máximo 255 caracteres'),
  placeholder: yup
    .string()
    .nullable()
    .default('')
    .min(0, 'O apelido deve ter no mínimo 0 caracteres')
    .max(255, 'O apelido deve ter no máximo 255 caracteres'),
  description: yup
    .string()
    .optional()
    .default('')
    .min(0, 'A descrição deve ter no mínimo 0 caracteres')
    .max(2000, 'A descrição deve ter no máximo 2000 caracteres')
    .nullable(),
  status: yup
    .string()
    .oneOf(['active', 'inactive', 'deleted'], 'Situação inválida')
    .default('active')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormProductSubGroupHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormProductSubGroupHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean
}

/**
 * FormProductSubGroup is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormProductSubGroup from './src/views/PostEdit/FormProductSubGroup.tsx'
 *
 * const MyComponent = () => (
 *   <FormProductSubGroup />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormProductSubGroup: FC<Props> = ({ onSubmit = () => {}, initialValues = {}, isLoading = false }) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        {formik?.values?.id !== undefined && (
          <Form.Group as={Col} md={12} className="mb-3">
            <Form.Label>ID</Form.Label>
            <Form.Control
              type='text'
              placeholder='Código do subgrupo do produto'
              name='id'
              value={formik?.values?.id?.toString() ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </Form.Group>
        )}
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o nome do subgrupo do produto'
            name='name'
            value={formik.values?.name ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Apelido</Form.Label>
          <Form.Control
            type='text'
            placeholder='O apelido é um nome humanizado para do subgrupo do produto, ele substitui o nome em algumas situações'
            name='placeholder'
            value={formik.values?.placeholder ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Visibilidade</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            as='textarea'
            rows={4}
            placeholder='Informe a descrição detalhada do subgrupo do produto'
            name='description'
            maxLength={255}
            value={formik.values?.description ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Col md={12} className="mb-3">
          <Button
            variant="link"
            style={{ marginRight: 10 }}
            onClick={() => navigate('/product-subgroups')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button type="submit" disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}>
            Salvar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormProductSubGroup
